import { useState } from "react";
import { useDateRange, PRESETS } from "@/lib/dateRange";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import { CalendarIcon } from "lucide-react";

// Short labels to match the reference design
const SHORT = { "7d": "7D", "30d": "30D", "90d": "90D", "mtd": "MTD", "ytd": "YTD" };

function fmtShort(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

export default function DateRangePicker() {
  const { preset, setPreset, setCustom, range } = useDateRange();
  const [open, setOpen] = useState(false);
  const isCustom = preset === "custom";

  const selectedRange = isCustom
    ? { from: range.start ? new Date(range.start) : undefined, to: range.end ? new Date(range.end) : undefined }
    : undefined;

  const onSelect = (r) => {
    if (!r) return;
    if (r.from && r.to) {
      const start = new Date(r.from); start.setHours(0, 0, 0, 0);
      const end = new Date(r.to); end.setHours(23, 59, 59, 999);
      setCustom(start.toISOString(), end.toISOString());
      setOpen(false);
    } else if (r.from) {
      const start = new Date(r.from); start.setHours(0, 0, 0, 0);
      setCustom(start.toISOString(), start.toISOString());
    }
  };

  return (
    <div
      data-testid="date-range-picker"
      className="inline-flex items-center bg-white rounded-full p-1 border border-black/[0.06] shadow-[0_1px_3px_rgba(0,0,0,0.03)] overflow-x-auto scrollbar-hide max-w-full"
    >
      {PRESETS.map((p) => (
        <button
          key={p.key}
          data-testid={`range-${p.key}`}
          onClick={() => setPreset(p.key)}
          className={`px-3 sm:px-4 py-1.5 rounded-full text-[12px] font-semibold transition-all whitespace-nowrap flex-shrink-0 ${
            preset === p.key
              ? "bg-[#A87552] text-white shadow-[0_2px_5px_rgba(100,67,49,0.22)]"
              : "text-[#3C3C43]/85 hover:text-[#1C1C1E]"
          }`}
        >
          {SHORT[p.key] || p.label}
        </button>
      ))}

      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            data-testid="range-custom"
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] font-semibold transition-all whitespace-nowrap flex-shrink-0 ${
              isCustom
                ? "bg-[#A87552] text-white shadow-[0_2px_5px_rgba(100,67,49,0.22)]"
                : "text-[#3C3C43]/85 hover:text-[#1C1C1E]"
            }`}
            aria-label="Custom range"
          >
            <CalendarIcon className="w-3.5 h-3.5" />
            {isCustom && <span>{fmtShort(range.start)} – {fmtShort(range.end)}</span>}
          </button>
        </PopoverTrigger>
        <PopoverContent
          data-testid="date-range-popover"
          align="end"
          className="w-auto p-0 rounded-2xl border border-black/[0.06] shadow-[0_20px_60px_rgba(0,0,0,0.12)]"
        >
          <div className="p-3 border-b border-black/[0.05]">
            <div className="text-[11px] uppercase tracking-wider text-[#8E8E93] font-medium">Pick a date range</div>
            <div className="text-[12.5px] text-[#3C3C43]/80 mt-0.5">
              {selectedRange?.from ? fmtShort(selectedRange.from.toISOString()) : "Start"}
              <span className="mx-1.5 text-[#8E8E93]">→</span>
              {selectedRange?.to ? fmtShort(selectedRange.to.toISOString()) : "End"}
            </div>
          </div>
          <Calendar mode="range" selected={selectedRange} onSelect={onSelect} numberOfMonths={2} defaultMonth={selectedRange?.from || new Date()} className="date-range-calendar p-3" />
          <div className="flex items-center justify-between gap-2 p-3 border-t border-black/[0.05]">
            <div className="text-[11px] text-[#8E8E93]">Click a start date, then an end date.</div>
            <div className="flex gap-2">
              <Button variant="ghost" size="sm" className="rounded-full text-[12px] h-8" onClick={() => { setPreset("30d"); setOpen(false); }}>Clear</Button>
              <Button size="sm" className="rounded-full bg-black text-white hover:bg-black/85 text-[12px] h-8" onClick={() => setOpen(false)} disabled={!isCustom}>Done</Button>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
